import type { ReactNode } from 'react';
import styled from 'styled-components';
import { SparkleIcon } from '@phosphor-icons/react';
import { SuggestedPromptChip } from '../SuggestedPromptChip';
import { colors, spacing, typography } from '../../theme-v2';

export interface ChatEmptyStatePrompt {
  /** Stable identifier passed back to `onPromptSelect`. */
  id: string;
  /** Visible chip label. */
  label: ReactNode;
  /** Optional icon shown inside the chip. */
  icon?: ReactNode;
}

export interface ChatEmptyStateProps {
  /** Hero title (e.g. "Ask the Knowledge Hub"). Consumer-translated. */
  title: ReactNode;
  /** Sub-copy under the title. Consumer-translated. */
  subtitle?: ReactNode;
  /** Optional hero icon. Defaults to a sparkle. */
  icon?: ReactNode;
  /** Suggested starter prompts shown as a chip row. */
  prompts?: ChatEmptyStatePrompt[];
  /**
   * Fired when the user clicks a suggested prompt chip. Receives the
   * full prompt object so the caller can use either the `id` or
   * `label`.
   */
  onPromptSelect?: (prompt: ChatEmptyStatePrompt) => void;
  className?: string;
}

/**
 * ChatEmptyState — placeholder shown inside `<ChatThread>` when there
 * are no messages yet. Hero icon + title + subtitle + a row of
 * suggested starter prompts.
 *
 * Stateless. All copy is consumer-supplied (no i18n inside the lib);
 * `onPromptSelect` lets the parent feed the chosen prompt back into
 * the input bar or directly submit it.
 */
export const ChatEmptyState = ({
  title,
  subtitle,
  icon,
  prompts,
  onPromptSelect,
  className,
}: ChatEmptyStateProps) => (
  <StyledWrapper className={className}>
    <StyledHero>
      <StyledIcon aria-hidden="true">
        {icon ?? <SparkleIcon size={36} weight="duotone" />}
      </StyledIcon>
      <StyledTitle>{title}</StyledTitle>
      {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
    </StyledHero>
    {prompts && prompts.length > 0 && (
      <StyledChipRow>
        {prompts.map((p) => (
          <SuggestedPromptChip
            key={p.id}
            icon={p.icon}
            onClick={() => onPromptSelect?.(p)}
          >
            {p.label}
          </SuggestedPromptChip>
        ))}
      </StyledChipRow>
    )}
  </StyledWrapper>
);

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: ${spacing.lg}px ${spacing.md}px;
`;

const StyledHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${spacing.sm}px;
  /* Center the hero block within the wrapper's free space (above the
     chip row, which sits at the bottom via the chip-row margin-top). */
  flex: 1;
  min-height: 0;
  max-width: 420px;
  margin: 0 auto;
`;

const StyledIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.accent.base};
  margin-bottom: ${spacing.xs}px;
`;

const StyledTitle = styled.h3`
  margin: 0;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.lineHeight.tight};
  color: ${colors.text.primary};
`;

const StyledSubtitle = styled.p`
  margin: 0;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  line-height: ${typography.lineHeight.medium};
  color: ${colors.text.secondary};
`;

const StyledChipRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.sm}px;
  width: 100%;
  /* SuggestedPromptChip is inline-flex (content-sized) — force each
     chip to stretch to fill its grid cell so the row spans full width. */
  > button {
    width: 100%;
  }
`;
